import { isInvalidString } from "src/validation/stringValidation";
import {
  isInvalidPurchaseOrderNumber,
  isInvalidQuantity,
  isInvalidCancellationReason
} from "src/validation/stringValidation";
import { isInvalidISODate, isDateInTheFuture, incrementDateByDays } from "src/helpers/dateHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { UpdatePurchaseOrderConfirmationFormDataKey, POProductConfirmationInputField } from "src/types";

class UpdatePurchaseOrderConfirmationFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<"purchaseOrderId"> | undefined {
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Enter a valid purchase order number (e.g., 'PO-2055')", "purchaseOrderId")
      : undefined;
  }

  private static validateEstShipDate(value: unknown): ValidationError<"estShipDate"> | undefined {
    if (!value) {
      return undefined;
    }

    if (isInvalidISODate(value)) {
      return new ValidationError("Enter a valid date (example format: '01/01/2026')", "estShipDate");
    }

    const dateValue = value as string;

    // Check if date is in the future
    if (new Date(dateValue) < new Date()) {
      return new ValidationError("Estimated ship date must be in the future", "estShipDate");
    }

    // Check if date is not over a month in the future
    if (new Date() > incrementDateByDays(new Date(dateValue), 30)) {
      return new ValidationError("Estimated ship date cannot be over a month in the future", "estShipDate");
    }

    return undefined;
  }

  private static validateEstDeliveryDate(value: unknown): ValidationError<"estDeliveryDate"> | undefined {
    if (!value) {
      return undefined;
    }

    if (isInvalidISODate(value)) {
      return new ValidationError("Enter a valid date (example format: '01/01/2026')", "estDeliveryDate");
    }

    // Check if date is in the future
    if (new Date(value as string) < new Date()) {
      return new ValidationError("Estimated delivery date must be in the future", "estDeliveryDate");
    }

    return undefined;
  }

  private static validateEstCollectionDate(value: unknown): ValidationError<"estCollectionDate"> | undefined {
    if (!value) {
      return undefined;
    }

    if (isInvalidISODate(value)) {
      return new ValidationError("Enter a valid date (example format: '01/01/2026')", "estCollectionDate");
    }

    // Check if date is in the future
    if (!isDateInTheFuture(value as string)) {
      return new ValidationError("Estimated collection date must be in the future", "estCollectionDate");
    }

    return undefined;
  }

  private static validateProductConfirmations(value: unknown): ValidationError<"productConfirmations"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    for (const arrayValue of value) {
      const { confirmedQty, estRestockDate, isDiscontinued, orderQty } = arrayValue as POProductConfirmationInputField;

      if (isInvalidQuantity(confirmedQty, 0)) {
        return new ValidationError(
          `Product confirmation has invalid quantity (cannot be '${confirmedQty}')`,
          "productConfirmations"
        );
      }

      // Calculate fulfillment rate
      const confirmed = parseFloat(confirmedQty) || 0;
      const ordered = parseFloat(orderQty) || 0;
      const fulfillmentRate = ordered === 0 ? 100 : (confirmed / ordered) * 100;

      // Require restock date if fulfillment is less than 50%
      if (fulfillmentRate < 50 && !estRestockDate && !isDiscontinued) {
        return new ValidationError(
          "Restock date is mandatory for products with fulfillment rate less than 50% unless marked as discontinued",
          "productConfirmations"
        );
      }

      if (estRestockDate) {
        if (isInvalidISODate(estRestockDate)) {
          return new ValidationError(
            "Product confirmation has invalid estimated restock date (example format: '01/01/2026')",
            "productConfirmations"
          );
        }

        // Check if restock date is in the future
        if (!isDateInTheFuture(estRestockDate)) {
          return new ValidationError("Estimated restock dates must be in the future", "productConfirmations");
        }
      }
    }

    return undefined;
  }

  private static validateAdditionalInfo(value: unknown): ValidationError<"additionalInfo"> | undefined {
    return value && isInvalidString(value, 0, 1200)
      ? new ValidationError("Additional information is too long or invalid", "additionalInfo")
      : undefined;
  }

  private static validateCancellationReason(value: unknown): ValidationError<"cancellationReason"> | undefined {
    return isInvalidCancellationReason(value?.toString())
      ? new ValidationError("Enter a valid cancellation reason", "cancellationReason")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    UpdatePurchaseOrderConfirmationFormDataKey,
    (inputValue: unknown) => ValidationError<UpdatePurchaseOrderConfirmationFormDataKey> | undefined
  > = {
    purchaseOrderId: UpdatePurchaseOrderConfirmationFormValidator.validatePurchaseOrderNumber,
    productConfirmations: UpdatePurchaseOrderConfirmationFormValidator.validateProductConfirmations,
    estShipDate: UpdatePurchaseOrderConfirmationFormValidator.validateEstShipDate,
    estDeliveryDate: UpdatePurchaseOrderConfirmationFormValidator.validateEstDeliveryDate,
    estCollectionDate: UpdatePurchaseOrderConfirmationFormValidator.validateEstCollectionDate,
    additionalInfo: UpdatePurchaseOrderConfirmationFormValidator.validateAdditionalInfo,
    cancellationReason: UpdatePurchaseOrderConfirmationFormValidator.validateCancellationReason
  };

  static validatePurchaseOrderFormData<T extends UpdatePurchaseOrderConfirmationFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      UpdatePurchaseOrderConfirmationFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { UpdatePurchaseOrderConfirmationFormValidator };
